import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { LiveTranscript } from '../components/LiveTranscript';
import { ResearchPeekPanel } from '../components/ResearchPeekPanel';
import { QualificationMetrics } from '../components/QualificationMetrics';
import { SuggestedQuestions } from '../components/SuggestedQuestions';
import { SolutionBuilder } from '../components/SolutionBuilder';
import { FrameworkSelector } from '../components/FrameworkSelector';
import { CoachingHighlights } from '../components/CoachingHighlights';
import { CalendarMeetings } from '../components/CalendarMeetings';
import { RecentCallsPeek } from '../components/RecentCallsPeek';
import { AdminViewOnlyPanel } from '../components/AdminViewOnlyPanel';
import { TokenBlockedModal } from '../components/TokenBlockedModal';
import { Mic, MicOff, User, LogOut, Settings, Loader2, Phone, PhoneOff, BarChart3, Globe, Building2, Sparkles, History, GitBranch, Package, Video, Link2, Wifi, WifiOff, Users, CreditCard, AlertCircle, MessageSquare, ChevronUp, PictureInPicture2, Check, AlertTriangle, Pin, PinOff, Target } from 'lucide-react';
import { toast } from 'sonner';
import { SupportButton } from '../components/SupportWidget';
import { PiPCoachingWindow } from '../components/PiPCoachingWindow';
import { FirstCallTour } from '../components/FirstCallTour';
import { TokenManagement } from '../components/TokenManagement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto-analysis interval in milliseconds
const AUTO_ANALYSIS_INTERVAL = 15000; // 15 seconds
const MIN_TRANSCRIPT_FOR_ANALYSIS = 2;

// Recognised meeting platforms — matched by hostname (case-insensitive)
const MEETING_PLATFORMS = [
  { id: 'zoom', name: 'Zoom', match: /(^|\.)zoom\.us$/i, example: 'https://us05web.zoom.us/j/1234567890' },
  { id: 'meet', name: 'Google Meet', match: /^meet\.google\.com$/i, example: 'https://meet.google.com/abc-defg-hij' },
  { id: 'teams', name: 'Microsoft Teams', match: /(^|\.)teams\.(microsoft|live)\.com$/i, example: 'https://teams.microsoft.com/l/meetup-join/...' },
];

/**
 * Validate a meeting link and detect its platform.
 * Lightweight — warns but never blocks (some companies self-host on
 * custom subdomains that don't match the standard regexes).
 *
 * Returns { normalized, platform, warning }
 *   normalized: URL with https:// auto-prepended if missing
 *   platform:   { id, name } if recognised, else null
 *   warning:    user-friendly string if something looks off, else null
 */
function validateMeetingLink(raw) {
  const trimmed = (raw || '').trim();
  if (!trimmed) return { normalized: '', platform: null, warning: null };

  // Auto-prepend https:// for bare URLs (e.g. "meet.google.com/abc-...")
  let normalized = trimmed;
  if (!/^https?:\/\//i.test(normalized)) {
    normalized = `https://${normalized}`;
  }

  let parsed;
  try {
    parsed = new URL(normalized);
  } catch (_e) {
    return {
      normalized,
      platform: null,
      warning: "This doesn't look like a valid URL — make sure you've copied the full meeting link.",
    };
  }

  if (parsed.protocol !== 'https:') {
    return {
      normalized,
      platform: null,
      warning: 'Meeting links should start with https:// — http:// links may fail.',
    };
  }

  const platform = MEETING_PLATFORMS.find(p => p.match.test(parsed.hostname));
  if (!platform) {
    return {
      normalized,
      platform: null,
      warning: "We don't recognise this as a Zoom, Google Meet, or Teams link. The bot may fail to join.",
    };
  }

  // Detect Teams "Meet Now" landing URLs — these are not real meeting rooms,
  // they're the page where Teams creates a meeting on the fly. The bot will
  // get rejected by Teams with `meeting_link_invalid`. Tell the user to start
  // the meeting first and copy the full link.
  const path = parsed.pathname || '';
  if (
    platform.id === 'teams' &&
    /\/(meet-now|meetnow|v2\/meet\/meet-now)\b/i.test(path)
  ) {
    return {
      normalized,
      platform: { id: platform.id, name: platform.name },
      warning: 'This looks like a Teams "Meet Now" landing page, not a meeting room link. Start the meeting first, then copy the share link (it will contain a meeting ID).',
    };
  }

  return { normalized, platform: { id: platform.id, name: platform.name }, warning: null };
}

// Analysis countdown indicator - shows time until next auto-analysis
function AnalysisCountdown({ isActive, isAnalyzing, lastAnalysisTime }) {
  const [secondsLeft, setSecondsLeft] = useState(15);

  useEffect(() => {
    if (!isActive) {
      setSecondsLeft(15);
      return;
    }

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastAnalysisTime) / 1000);
      const remaining = Math.max(0, 15 - elapsed);
      setSecondsLeft(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, lastAnalysisTime]);

  if (!isActive) return null;

  if (isAnalyzing) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-brand-muted border border-brand-accent-muted rounded-full" data-testid="analysis-active-indicator">
        <Loader2 className="w-3 h-3 text-brand-primary animate-spin" />
        <span className="text-xs font-medium text-brand-primary">Analyzing...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-100 border border-zinc-200 rounded-full" data-testid="analysis-countdown">
      <div className="relative w-4 h-4">
        <svg className="w-4 h-4 -rotate-90" viewBox="0 0 16 16">
          <circle cx="8" cy="8" r="6" fill="none" stroke="#e4e4e7" strokeWidth="2" />
          <circle 
            cx="8" cy="8" r="6" fill="none" stroke="#0E4E5A" strokeWidth="2"
            strokeDasharray={`${(secondsLeft / 15) * 37.7} 37.7`}
            className="transition-all duration-1000"
          />
        </svg>
      </div>
      <span className="text-xs font-medium text-zinc-600">
        {secondsLeft > 0 ? `${secondsLeft}s` : 'Soon...'}
      </span>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check for follow-up call params
  const followUpParentId = searchParams.get('follow_up');
  const followUpFramework = searchParams.get('framework');
  const followUpProspect = searchParams.get('prospect');
  const followUpCompany = searchParams.get('company');
  const prefillWebsite = searchParams.get('website');
  
  // State
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState(followUpFramework || 'bant');
  const [session, setSession] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [prospectName, setProspectName] = useState(followUpProspect ? decodeURIComponent(followUpProspect) : '');
  const [companyName, setCompanyName] = useState(followUpCompany ? decodeURIComponent(followUpCompany) : '');
  const [companyWebsite, setCompanyWebsite] = useState(prefillWebsite ? decodeURIComponent(prefillWebsite) : '');
  const [companyContext, setCompanyContext] = useState('');
  const [isResearchingCompany, setIsResearchingCompany] = useState(false);
  const [callStarted, setCallStarted] = useState(false);
  const [lastAnalysisTime, setLastAnalysisTime] = useState(0);
  const [autoAnalysisEnabled, setAutoAnalysisEnabled] = useState(true);
  const [parentSessionId, setParentSessionId] = useState(followUpParentId || null);
  
  // Company settings & products
  const [companySettings, setCompanySettings] = useState(null);
  const [products, setProducts] = useState([]);
  
  // Meeting bot state
  const [meetingLink, setMeetingLink] = useState('');
  const [inputMode, setInputMode] = useState('meeting'); // 'meeting' | 'mic'
  const [meetingBotId, setMeetingBotId] = useState(null);
  const [meetingBotStatus, setMeetingBotStatus] = useState(null); // joining, in_meeting, ended, error
  const [meetingBotError, setMeetingBotError] = useState(null); // friendly error message from backend
  const [tokenBlockedInfo, setTokenBlockedInfo] = useState(null); // {message, admin_emails, rep_balance, shared_pool}
  const [isJoiningMeeting, setIsJoiningMeeting] = useState(false);
  const [resumableSession, setResumableSession] = useState(null);

  // Pinned question (top of suggested-questions list, survives analysis refreshes)
  const [pinnedQuestion, setPinnedQuestion] = useState(null);
  
  // Refs for speech recognition and auto-analysis
  const recognitionRef = useRef(null);
  const transcriptBufferRef = useRef('');
  const autoAnalysisRef = useRef(null);
  const meetingPollRef = useRef(null);
  const meetingSSERef = useRef(null);
  const meetingStatusPollRef = useRef(null);
  const lastTranscriptCountRef = useRef(0);
  const sessionRef = useRef(null);
  const transcriptRef = useRef([]);

  // Keep refs in sync with state
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  // Refs that the auto-analysis interval reads, so the interval can live for
  // the lifetime of the call WITHOUT being torn down + recreated every time
  // these values change. Without this, the 15s countdown was effectively
  // restarting on every new transcript entry → analysis fired far less often
  // than every 15s during continuous speech.
  const isAnalyzingRef = useRef(false);
  const selectedFrameworkRef = useRef(null);
  const companyContextRef = useRef('');
  
  // Manual override tracking
  const [manualOverridesUsed, setManualOverridesUsed] = useState(0);
  const [planLimits, setPlanLimits] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showManagerInvitePrompt, setShowManagerInvitePrompt] = useState(false);
  const [orgOnboarding, setOrgOnboarding] = useState(null);  // { has_completed_first_call, referral_bonus_granted, member_count }
  // First-call gating: hide Admin/Team menu items until the org has at least one completed call.
  // Owners are still admins under the hood — we just simplify what they SEE on first login.
  const isFirstCallExperience = orgOnboarding && !orgOnboarding.has_completed_first_call;
  // Admin/manager seats are view-only once the org has sales reps, or on any paid plan
  const isViewOnlySeat = (user?.role === 'admin' || user?.role === 'manager') &&
    (orgOnboarding?.has_sales_reps || (planLimits?.plan && planLimits.plan !== 'starter'));
  // 5-step guided tour, shown only on the very first call. Dismissal is persisted in localStorage.
  const [tourActive, setTourActive] = useState(false);
  useEffect(() => {
    if (!isFirstCallExperience) { setTourActive(false); return; }
    if (typeof window === 'undefined') return;
    const dismissed = window.localStorage.getItem('pg_first_call_tour_done') === '1';
    setTourActive(!dismissed);
  }, [isFirstCallExperience]);
  const TOUR_STEPS = [
    { title: "You're live!", body: 'PipeGuide is listening to the call and will start scoring qualification in real-time. Here are the four things to watch.' },
    { testId: 'qualification-metrics', title: 'Live qualification score', body: 'Your BANT / MEDDIC / SPIN score recalculates every ~15 seconds as new transcript lands. Aim for 80+ before proposing next steps.' },
    { testId: 'questions-focal-area', title: 'AI-suggested probing questions', body: 'When the score drops in a category, the AI surfaces a probing question to help you dig deeper. Click any to copy it.' },
    { testId: 'transcript-toggle', title: 'Live transcript', body: 'Toggle the transcript panel to see exactly what was said. Speaker labels and timestamps are included for review later.' },
    { testId: 'end-call-btn', title: 'End the call', body: "When you're done, hit End. You'll get a PDF summary plus a coaching breakdown emailed to you — and the workspace setup unlocks." },
  ];
  
  // PiP Coaching Window state
  const [pipOpen, setPipOpen] = useState(false);
  const [pipCallEnded, setPipCallEnded] = useState(false);

  // Pre-call research shortcut: does a recent research exist for the typed company?
  const [researchLookup, setResearchLookup] = useState(null);
  const [callResearch, setCallResearch] = useState(null);
  useEffect(() => {
    if (!companyName || companyName.trim().length < 3 || orgOnboarding?.research_enabled === false) {
      setResearchLookup(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/research/lookup`, { params: { company: companyName.trim() } });
        setResearchLookup(data);
      } catch (e) {
        setResearchLookup(null);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [companyName, orgOnboarding]);

  // Load the full research for the in-call peek panel once a call starts
  useEffect(() => {
    if (!callStarted || !researchLookup?.found) {
      setCallResearch(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/research/${researchLookup.id}`);
        if (!cancelled) setCallResearch(data);
      } catch (e) {
        // Non-fatal — panel simply doesn't render
      }
    })();
    return () => { cancelled = true; };
  }, [callStarted, researchLookup]);

  // Fetch org onboarding state (controls sales-first UI gating for new orgs)
  useEffect(() => {
    if (!user?.org_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/org`);
        if (!cancelled) setOrgOnboarding(data.onboarding || null);
      } catch (e) {
        // Non-fatal: dashboard still works without this
      }
    })();
    return () => { cancelled = true; };
  }, [user?.org_id]);

  // Fetch plan limits for manual override cap
  useEffect(() => {
    const fetchLimits = async () => {
      try {
        const { data } = await axios.get(`${API}/billing/subscription`);
        setPlanLimits(data);
      } catch (e) {
        // Default to starter limits
        setPlanLimits({ manual_overrides_per_meeting: 1 });
      }
    };
    fetchLimits();
  }, []);

  // Fetch frameworks
  useEffect(() => {
    const fetchFrameworks = async () => {
      try {
        const { data } = await axios.get(`${API}/frameworks`);
        setFrameworks(data);
        if (data.length > 0 && !selectedFramework) {
          setSelectedFramework(data[0].id);
        }
      } catch (e) {
        console.error('Failed to fetch frameworks:', e);
        toast.error('Failed to load frameworks');
      }
    };
    fetchFrameworks();
  }, [selectedFramework]);

  // Fetch company settings & products
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const [settingsRes, productsRes] = await Promise.all([
          axios.get(`${API}/company/settings`),
          axios.get(`${API}/products`)
        ]);
        setCompanySettings(settingsRes.data);
        setProducts(productsRes.data);
      } catch (e) {
        console.error('Failed to fetch company data:', e);
      }
    };
    fetchCompanyData();
  }, []);

  // Check for active session that can be resumed
  useEffect(() => {
    const checkForActiveSession = async () => {
      if (callStarted || session) return;
      try {
        const { data: sessions } = await axios.get(`${API}/sessions`);
        const active = sessions.find(s => s.status === 'active');
        if (active) {
          setResumableSession(active);
        }
      } catch (e) {
        // No active session
      }
    };
    checkForActiveSession();
  }, []);

  // Attach to a bot-scheduled calendar session (from the reminder email link)
  useEffect(() => {
    const attachId = searchParams.get('attach_session');
    if (!attachId || callStarted || session) return;
    (async () => {
      try {
        const { data: s } = await axios.get(`${API}/sessions/${attachId}`);
        const bot = s.meeting_bot || {};
        setSession(s);
        setCallStarted(true);
        setSelectedFramework(s.framework_id || 'bant');
        setProspectName(s.prospect_name || '');
        setCompanyName(s.company_name || '');
        setInputMode('meeting');
        if (bot.bot_id) {
          setMeetingBotId(bot.bot_id);
          setMeetingBotStatus(bot.status || 'scheduled');
          startMeetingPolling(bot.bot_id, s.id);
        }
        const entries = (s.transcript || []).map((t, i) => ({
          id: `restored-${i}`,
          text: t.text,
          speaker: t.speaker || 'rep',
          speakerName: t.speaker_name,
          timestamp: t.timestamp || new Date().toISOString(),
        }));
        if (entries.length > 0) setTranscript(entries);
        window.history.replaceState({}, '', window.location.pathname);
        toast.success('Live call screen ready — the bot joins your meeting automatically');
      } catch (e) {
        toast.error('Could not open that scheduled call');
        window.history.replaceState({}, '', window.location.pathname);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Resume an active call session
  const resumeCall = (activeSession) => {
    setSession(activeSession);
    setCallStarted(true);
    setSelectedFramework(activeSession.framework_id || selectedFramework);
    setProspectName(activeSession.prospect_name || '');
    setCompanyName(activeSession.company_name || '');
    // Always resume in mic mode — meeting bot connection is lost on disconnect
    setInputMode('mic');
    setMeetingBotId(null);
    setMeetingBotStatus(null);

    // Load transcript from backend
    const transcriptEntries = activeSession.transcript || [];
    if (transcriptEntries.length > 0) {
      const loaded = transcriptEntries.map((t, i) => ({
        id: `restored-${i}`,
        text: t.text,
        speaker: t.speaker || 'rep',
        timestamp: t.timestamp || new Date().toISOString(),
      }));
      setTranscript(loaded);
      lastTranscriptCountRef.current = 0;
    }

    // Load last analysis if scores exist
    if (activeSession.qualification_scores && Object.keys(activeSession.qualification_scores).length > 0) {
      const scores = activeSession.qualification_scores;
      const overall = Object.values(scores).length > 0 
        ? Math.round(Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length) 
        : 0;
      setAnalysis({
        scores,
        overall_score: overall,
        suggested_questions: activeSession.suggested_questions || [],
        gaps: [],
        key_insights: [],
      });
    }

    setResumableSession(null);
    toast.success('Call resumed — click Start Listening to continue');
  };

  // Initialize Speech Recognition (once on mount)
  const isListeningRef = useRef(false);
  
  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onresult = (event) => {
      let finalTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptText = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcriptText;
        }
      }
      
      if (finalTranscript) {
        transcriptBufferRef.current += ' ' + finalTranscript;
        const newEntry = {
          id: Date.now().toString(),
          text: finalTranscript.trim(),
          speaker: 'rep',
          timestamp: new Date().toISOString()
        };
        setTranscript(prev => [...prev, newEntry]);
        // Persist to backend
        const sid = sessionRef.current?.id;
        if (sid) {
          axios.post(`${API}/sessions/${sid}/transcript`, {
            text: finalTranscript.trim(),
            speaker: 'rep'
          }).catch(() => {});
        }
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. Please allow microphone access.');
      }
    };
    
    recognition.onend = () => {
      // Auto-restart if still supposed to be listening
      if (isListeningRef.current) {
        try { recognition.start(); } catch (e) { /* already started */ }
      }
    };
    
    recognitionRef.current = recognition;
    
    return () => {
      try { recognition.stop(); } catch (e) { /* not started */ }
    };
  }, []); // Initialize ONCE

  // Research company from website
  const researchCompany = async () => {
    if (!companyWebsite.trim()) {
      toast.error('Please enter a company website');
      return;
    }
    
    setIsResearchingCompany(true);
    try {
      const { data } = await axios.post(
        `${API}/research-company`,
        { website_url: companyWebsite },
        
      );
      setCompanyContext(data.company_context);
      toast.success('Company context loaded! This will help surface relevant questions.');
    } catch (e) {
      console.error('Company research failed:', e);
      toast.error('Failed to research company');
    } finally {
      setIsResearchingCompany(false);
    }
  };

  // Join meeting via MeetStream bot
  const joinMeeting = async (sessionId) => {
    setIsJoiningMeeting(true);
    try {
      const { data } = await axios.post(
        `${API}/meeting/join`,
        {
          meeting_link: meetingLink,
          session_id: sessionId,
          bot_name: companySettings?.company_name ? `${companySettings.company_name} Assistant` : 'PipeGuide Assistant'
        },
        
      );
      
      setMeetingBotId(data.bot_id);
      setMeetingBotStatus('joining');
      setMeetingBotError(null);
      // Surface the backend's message — tells the rep whether live transcription
      // is active or whether they'll receive a post-call transcript instead.
      toast.success(data.message || 'Bot is joining the meeting...');
      
      // Start polling for bot status and transcript
      startMeetingPolling(data.bot_id, sessionId);
    } catch (e) {
      console.error('Failed to join meeting:', e);
      const status = e.response?.status;
      const rawDetail = e.response?.data?.detail;
      const detailIsObject = rawDetail && typeof rawDetail === 'object';
      const detailText = detailIsObject ? rawDetail.message : (rawDetail || 'Failed to join meeting');
      // Persist the error on local state so the rep can SEE that the bot
      // never joined (rather than the silent "0 messages" empty-state).
      setMeetingBotStatus('error');
      setMeetingBotError(detailText);
      if (status === 402 && detailIsObject && rawDetail.code === 'token_limit_reached') {
        // Show the rich "out of tokens" modal — admin contact + in-person fallback
        setTokenBlockedInfo(rawDetail);
      } else if (status === 402) {
        toast.error(detailText, {
          duration: 15000,
          action: {
            label: 'Open Billing',
            onClick: () => { window.location.href = '/billing'; },
          },
        });
      } else {
        toast.error(`Bot failed to join: ${detailText}`, { duration: 15000 });
      }
    } finally {
      setIsJoiningMeeting(false);
    }
  };

  // Stop both SSE and polling (called on call end / errors).
  const stopMeetingLiveStream = () => {
    if (meetingSSERef.current) {
      try { meetingSSERef.current.close(); } catch (_e) { /* noop */ }
      meetingSSERef.current = null;
    }
    if (meetingPollRef.current) {
      clearInterval(meetingPollRef.current);
      meetingPollRef.current = null;
    }
    if (meetingStatusPollRef.current) {
      clearInterval(meetingStatusPollRef.current);
      meetingStatusPollRef.current = null;
    }
  };

  // Start live updates: SSE first (sub-second latency), polling fallback.
  const startMeetingPolling = (botId, sessionId) => {
    stopMeetingLiveStream();
    let cursor = 0;
    const handleEntry = (entry) => {
      const fresh = {
        id: `meeting-${cursor}-${entry.timestamp || Date.now()}`,
        text: entry.text || entry.words || '',
        speaker: entry.speaker || 'participant',
        speaker_name: entry.speaker_name,
        timestamp: entry.timestamp || new Date().toISOString(),
        source: entry.source || 'meeting_bot',
      };
      cursor += 1;
      setTranscript(prev => [...prev, fresh]);
    };
    const handleStatusChange = (newStatus, message) => {
      const s = (newStatus || '').toLowerCase().replace(' ', '_');
      if (!s) return;
      setMeetingBotStatus(prev => {
        if (prev !== s) {
          if (s === 'in_meeting' || s === 'in_call') toast.success('Bot joined the meeting');
          else if (s === 'ended' || s === 'done') toast.info('Bot left the meeting');
          else if (s === 'error' || s === 'fatal') {
            setMeetingBotError(message || 'Bot error');
            toast.error(message || 'Bot error', { duration: 12000 });
          }
        }
        return s;
      });
      if (['ended', 'error', 'done', 'fatal'].includes(s)) {
        stopMeetingLiveStream();
      }
    };

    // ── Attempt SSE first ─────────────────────────────────────────────────
    const token = localStorage.getItem('token') || '';
    const sseUrl = `${API}/sessions/${sessionId}/stream?token=${encodeURIComponent(token)}`;
    let sseReady = false;
    let pollingStarted = false;

    const startPollingFallback = () => {
      if (pollingStarted) return;
      pollingStarted = true;
      // Mirror previous polling behavior — uses /live?since=N + status check
      let pollCursor = cursor; // continue from where SSE left off
      let tickCount = 0;
      meetingPollRef.current = setInterval(async () => {
        tickCount += 1;
        try {
          const { data: live } = await axios.get(
            `${API}/sessions/${sessionId}/live?since=${pollCursor}`
          );
          const newEntries = Array.isArray(live?.new_entries) ? live.new_entries : [];
          newEntries.forEach((e) => { handleEntry(e); pollCursor += 1; });
          const embeddedStatus = live?.meeting_bot?.status;
          if (embeddedStatus) handleStatusChange(embeddedStatus, live?.meeting_bot?.error_message);
          if (tickCount % 3 === 0) {
            try {
              const { data: statusData } = await axios.get(`${API}/meeting/${botId}/status`);
              handleStatusChange(statusData.status, statusData.message);
            } catch (_e) { /* noop */ }
          }
        } catch (_e) { /* noop */ }
      }, 1500);
    };

    // ── Slow status safety net ────────────────────────────────────────────
    // Runs even when SSE is connected: Recall status webhooks are workspace-
    // level config and may be missing, in which case SSE never delivers
    // status transitions. This queries Recall directly every 10s so the UI
    // reliably moves joining → in_meeting → ended.
    if (!meetingStatusPollRef.current) {
      meetingStatusPollRef.current = setInterval(async () => {
        try {
          const { data: statusData } = await axios.get(`${API}/meeting/${botId}/status`);
          if (statusData?.status && statusData.status !== 'unknown') {
            handleStatusChange(statusData.status, statusData.message);
          }
        } catch (_e) { /* noop */ }
      }, 10000);
    }

    try {
      const es = new EventSource(sseUrl);
      meetingSSERef.current = es;
      // If SSE doesn't connect within 4s, fall back to polling.
      const fallbackTimer = setTimeout(() => {
        if (!sseReady) {
          console.warn('SSE did not open within 4s — falling back to polling');
          try { es.close(); } catch (_e) { /* noop */ }
          meetingSSERef.current = null;
          startPollingFallback();
        }
      }, 4000);

      es.addEventListener('snapshot', () => {
        // Backend has replayed historical entries via `transcript` events
        // below; nothing extra to do here. Used in future for richer init.
      });
      es.addEventListener('transcript', (evt) => {
        sseReady = true;
        clearTimeout(fallbackTimer);
        try {
          const data = JSON.parse(evt.data);
          if (data.entry) handleEntry(data.entry);
        } catch (_e) { /* noop */ }
      });
      es.addEventListener('status', (evt) => {
        sseReady = true;
        clearTimeout(fallbackTimer);
        try {
          const data = JSON.parse(evt.data);
          handleStatusChange(data.status, data.error_message);
        } catch (_e) { /* noop */ }
      });
      es.onopen = () => {
        sseReady = true;
        clearTimeout(fallbackTimer);
      };
      es.onerror = () => {
        // EventSource auto-reconnects, but if it can't open at all → poll.
        if (!sseReady) {
          clearTimeout(fallbackTimer);
          try { es.close(); } catch (_e) { /* noop */ }
          meetingSSERef.current = null;
          startPollingFallback();
        }
      };
    } catch (_e) {
      // EventSource not supported (very old browser) → polling
      startPollingFallback();
    }
  };

  // Leave meeting
  const leaveMeeting = async () => {
    if (!meetingBotId) return;
    
    try {
      await axios.post(`${API}/meeting/${meetingBotId}/leave`, {});
    } catch (e) {
      console.error('Failed to leave meeting:', e);
    }
    
    stopMeetingLiveStream();
    setMeetingBotStatus('ended');
  };

  // Start call session
  const startCall = async () => {
    try {
      const params = new URLSearchParams({
        framework_id: selectedFramework,
        ...(prospectName && { prospect_name: prospectName }),
        ...(companyName && { company_name: companyName }),
        ...(companyWebsite && { company_website: companyWebsite }),
        ...(parentSessionId && { parent_session_id: parentSessionId })
      });
      
      const { data } = await axios.post(
        `${API}/sessions?${params}`,
        {},
        
      );
      
      setSession({ ...data, company_context: companyContext });
      setCallStarted(true);
      setTranscript([]);
      setAnalysis(null);
      setLastAnalysisTime(0);
      setManualOverridesUsed(0);
      lastTranscriptCountRef.current = 0;
      transcriptBufferRef.current = '';
      
      // If meeting mode, join the meeting
      if (inputMode === 'meeting' && meetingLink.trim()) {
        await joinMeeting(data.id);
      }
      
      const callType = parentSessionId ? `${companySettings?.call_type_labels?.follow_up_call || 'Follow-up call'} #${data.call_number}` : 'Call session';
      const startMsg = inputMode === 'meeting'
        ? ' — bot joining meeting'
        : ' — analysis will run automatically';
      toast.success(`${callType} started${startMsg}`);
      
      // Clear URL params after starting
      if (parentSessionId) {
        navigate('/', { replace: true });
      }
    } catch (e) {
      console.error('Failed to start session:', e);
      const detail = e.response?.data?.detail || 'Failed to start call session';
      toast.error(detail);
      // When the email-verification gate blocks the user, send them straight
      // to the Verify Email screen instead of leaving them stuck on the form.
      if (typeof detail === 'string' && /verify your email/i.test(detail)) {
        navigate('/verify-email');
      }
    }
  };

  // End call session
  const endCall = async () => {
    // Capture the session id BEFORE we wipe local state so we can navigate
    // the rep to the post-call summary at the end. This avoids the jarring
    // "back to home" jump and keeps the rep in the natural follow-up flow.
    const endedSessionId = sessionRef.current?.id || session?.id;

    if (session) {
      try {
        await axios.post(`${API}/sessions/${session.id}/end`, {});
        toast.success('Call session ended');

        // Check if this is the user's first call — prompt to invite manager
        try {
          const { data: sessions } = await axios.get(`${API}/sessions`);
          const completedCount = sessions.filter(s => s.status === 'completed').length;
          if (completedCount === 1) {
            // Check if org already has a manager
            const { data: members } = await axios.get(`${API}/org/members`);
            const hasManager = members.some(m => m.role === 'manager');
            if (!hasManager) {
              setShowManagerInvitePrompt(true);
            }
          }
        } catch (e) {
          // Non-critical — silently skip
        }
      } catch (e) {
        console.error('Failed to end session:', e);
      }
    }
    
    // Leave meeting if bot is active
    if (meetingBotId && meetingBotStatus !== 'ended') {
      await leaveMeeting();
    }
    
    // Clear meeting polling + SSE
    stopMeetingLiveStream();
    
    // Clear auto-analysis interval
    if (autoAnalysisRef.current) {
      clearInterval(autoAnalysisRef.current);
      autoAnalysisRef.current = null;
    }
    
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
    
    setIsListening(false);
    setCallStarted(false);
    setSession(null);
    setCompanyContext('');
    setMeetingBotId(null);
    setMeetingBotStatus(null);
    setMeetingBotError(null);
    setMeetingLink('');
    setPinnedQuestion(null);
    
    // Flip PiP coach into "Call ended" state (user can close manually)
    if (pipOpen) {
      setPipCallEnded(true);
    }

    // Route the rep to the post-call summary so they land naturally in the
    // follow-up flow (generate summary → review → push to CRM) instead of
    // bouncing back to the home dashboard.
    if (endedSessionId) {
      navigate(`/call/${endedSessionId}`);
    }
  };

  // Toggle listening
  const toggleListening = () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
      toast.success('Listening started - speak into your microphone');
    }
  };

  // Analyze conversation
  const analyzeConversation = useCallback(async (showToast = false) => {
    const currentTranscript = transcriptRef.current;
    const currentSession = sessionRef.current;
    if (!currentSession || currentTranscript.length < MIN_TRANSCRIPT_FOR_ANALYSIS) return;
    // Guard via ref instead of state so concurrent calls from the interval +
    // manual trigger don't race the React re-render.
    if (isAnalyzingRef.current) return;
    isAnalyzingRef.current = true;
    setIsAnalyzing(true);
    try {
      const transcriptText = currentTranscript.map(t => `${t.speaker === 'rep' ? 'Sales Rep' : 'Prospect'}: ${t.text}`).join('\n');
      
      const { data } = await axios.post(
        `${API}/analyze`,
        {
          session_id: currentSession.id,
          transcript_text: transcriptText,
          framework_id: selectedFrameworkRef.current,
          company_context: companyContextRef.current || currentSession.company_context
        },
        
      );
      
      setAnalysis(data);
      setAnalysisError(null);
      setLastAnalysisTime(Date.now());
      lastTranscriptCountRef.current = currentTranscript.length;
      
      if (showToast) {
        toast.success('Analysis updated');
      }
    } catch (e) {
      console.error('Analysis failed:', e);
      const errMsg = e.response?.data?.detail || 'Analysis failed — retrying soon';
      setAnalysisError(errMsg.includes('RateLimit') ? 'AI service busy — will retry automatically' : errMsg);
      if (showToast) {
        toast.error('Failed to analyze conversation');
      }
    } finally {
      isAnalyzingRef.current = false;
      setIsAnalyzing(false);
    }
  }, []);

  // Keep refs in sync so the stable callback above always reads fresh values
  useEffect(() => { selectedFrameworkRef.current = selectedFramework; }, [selectedFramework]);
  useEffect(() => { companyContextRef.current = companyContext; }, [companyContext]);

  // AUTO-ANALYSIS: fires every 15s for the entire active-call lifetime.
  // Deliberately depends ONLY on activation signals — NOT on transcript.length,
  // lastAnalysisTime, or analyzeConversation — so the interval is never torn
  // down/recreated mid-call (which previously caused the 15s timer to keep
  // restarting and made analyses fire far less often than every 15s).
  useEffect(() => {
    const isActive = inputMode === 'meeting'
      ? (callStarted && ['in_meeting', 'in_call'].includes(meetingBotStatus))
      : (callStarted && isListening);
    
    if (!isActive || !autoAnalysisEnabled) {
      if (autoAnalysisRef.current) {
        clearInterval(autoAnalysisRef.current);
        autoAnalysisRef.current = null;
      }
      return;
    }

    // Avoid double-creating if the interval is already running for this call.
    if (autoAnalysisRef.current) return;

    autoAnalysisRef.current = setInterval(() => {
      const tx = transcriptRef.current;
      // Fire whenever we have enough transcript AND there's been at least one
      // new utterance since the last successful analysis. The transcript-count
      // gate prevents wasted GPT calls when the call goes silent, but if even
      // a single new line has landed since last analysis we re-run so scores
      // and questions stay fresh.
      if (tx.length >= MIN_TRANSCRIPT_FOR_ANALYSIS && tx.length > lastTranscriptCountRef.current) {
        analyzeConversation(false);
      }
    }, AUTO_ANALYSIS_INTERVAL);
    
    return () => {
      if (autoAnalysisRef.current) {
        clearInterval(autoAnalysisRef.current);
        autoAnalysisRef.current = null;
      }
    };
  }, [callStarted, isListening, autoAnalysisEnabled, inputMode, meetingBotStatus, analyzeConversation]);

  // Trigger initial analysis as soon as we have enough transcript.
  // Use `>=` (was `===`) so we don't miss the trigger when multiple entries
  // arrive between renders.
  useEffect(() => {
    if (callStarted && transcript.length >= MIN_TRANSCRIPT_FOR_ANALYSIS && !analysis && !isAnalyzingRef.current) {
      analyzeConversation(false);
    }
  }, [callStarted, transcript.length, analysis, analyzeConversation]);

  // Add solution note
  const handleAddNote = async (noteData) => {
    if (!session) return;
    
    try {
      await axios.post(
        `${API}/sessions/${session.id}/solution-note`,
        noteData,
        
      );
      
      // Refresh session
      const { data } = await axios.get(`${API}/sessions/${session.id}`);
      setSession(prev => ({ ...prev, solution_notes: data.solution_notes }));
      toast.success('Note added');
    } catch (e) {
      console.error('Failed to add note:', e);
      toast.error('Failed to add note');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const currentFramework = frameworks.find(f => f.id === selectedFramework);

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard">
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white px-3 sm:px-6">
        <div className="h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {companySettings?.logo_url ? (
              <img 
                src={companySettings.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${companySettings.logo_url}` : companySettings.logo_url} 
                alt={companySettings.company_name || 'Company'} 
                className="h-7 max-w-[80px] sm:max-w-[120px] object-contain flex-shrink-0"
                data-testid="company-logo"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div 
              className={`w-7 h-7 flex items-center justify-center flex-shrink-0 ${companySettings?.logo_url ? 'hidden' : ''}`}
              data-testid="default-logo"
            >
              <img src="/pipeguide-logo.png" alt="PipeGuide" className="h-7 w-7 object-contain" />
            </div>
            <span className="text-base sm:text-xl font-bold truncate" style={{ fontFamily: 'var(--font-heading)' }}>
              {companySettings?.company_name || 'PipeGuide'}
            </span>
          
            {callStarted && (
              <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                {inputMode === 'meeting' ? (
                  <>
                    <div className={`w-2 h-2 rounded-full ${
                      ['in_meeting', 'in_call'].includes(meetingBotStatus) ? 'bg-green-500 live-pulse' :
                      meetingBotStatus === 'joining' ? 'bg-yellow-500 animate-pulse' :
                      ['error', 'fatal'].includes(meetingBotStatus) ? 'bg-red-500' : 'bg-zinc-300'
                    }`} />
                    <span className="text-xs font-medium text-zinc-600 hidden sm:inline" data-testid="meeting-status">
                      {['in_meeting', 'in_call'].includes(meetingBotStatus) ? 'BOT IN MEETING' :
                       meetingBotStatus === 'joining' ? 'JOINING...' :
                       ['ended', 'done'].includes(meetingBotStatus) ? 'BOT LEFT' :
                       ['error', 'fatal'].includes(meetingBotStatus) ? 'ERROR' : 'CONNECTING'}
                    </span>
                  </>
                ) : (
                  <>
                    <div className={`w-2 h-2 rounded-full ${isListening ? 'bg-red-500 live-pulse' : 'bg-zinc-300'}`} />
                    <span className="text-xs font-medium text-zinc-600" data-testid="call-status">
                      {isListening ? 'LIVE' : 'Paused'}
                    </span>
                  </>
                )}
                {autoAnalysisEnabled && (isListening || meetingBotStatus === 'in_meeting') && (
                  <div className="hidden sm:flex items-center gap-1 text-xs text-brand-primary bg-brand-muted px-2 py-1 rounded" data-testid="auto-analysis-indicator">
                    <Sparkles className="w-3 h-3 animate-pulse" />
                    Auto-analyzing every 15s
                    {isAnalyzing && <Loader2 className="w-3 h-3 ml-1 animate-spin" />}
                  </div>
                )}
                {analysisError && (
                  <div className="hidden sm:flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded" data-testid="analysis-error">
                    <AlertCircle className="w-3 h-3" />
                    {analysisError}
                  </div>
                )}
              </div>
            )}
          </div>

        <div className="flex items-center gap-3">
          {/* Token balance pill — always visible to reps and admins */}
          <TokenManagement mode="compact" isAdmin={user?.role === 'admin'} />

          {callStarted && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setPipCallEnded(false); setPipOpen(true); }}
                className="rounded-md text-xs hidden sm:flex"
                data-testid="open-pip-coach-btn"
                title="Pop out a floating coaching window (stays on top of Zoom/Teams)"
              >
                <PictureInPicture2 className="w-3 h-3 mr-1" />
                Pop Out Coach
              </Button>
              <Button
                variant={autoAnalysisEnabled ? "default" : "outline"}
                size="sm"
                onClick={() => setAutoAnalysisEnabled(!autoAnalysisEnabled)}
                className="rounded-md text-xs"
                data-testid="toggle-auto-analysis-btn"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                {autoAnalysisEnabled ? 'Auto: ON' : 'Auto: OFF'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const maxOverrides = planLimits?.manual_overrides_per_meeting || 1;
                  if (manualOverridesUsed >= maxOverrides) {
                    alert(`Manual override limit reached (${maxOverrides}/meeting). Upgrade your plan for more.`);
                    return;
                  }
                  setManualOverridesUsed(prev => prev + 1);
                  analyzeConversation(true);
                }}
                disabled={isAnalyzing || transcript.length < MIN_TRANSCRIPT_FOR_ANALYSIS}
                className="rounded-md"
                data-testid="analyze-btn"
              >
                {isAnalyzing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <BarChart3 className="w-4 h-4 mr-2" />
                )}
                {isAnalyzing ? 'Analyzing...' : `Analyze Now (${manualOverridesUsed}/${planLimits?.manual_overrides_per_meeting || 1})`}
              </Button>
            </>
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-md" data-testid="user-menu-btn">
                <User className="w-4 h-4 mr-2" />
                {user?.name}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled>
                <span className="text-zinc-500">{user?.email}</span>
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <span className="text-xs uppercase tracking-wide text-zinc-400">
                  {user?.role}
                </span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/history" data-testid="history-link">
                  <History className="w-4 h-4 mr-2" />
                  Call History
                </Link>
              </DropdownMenuItem>
              {orgOnboarding?.research_enabled !== false && (
                <DropdownMenuItem asChild>
                  <Link to="/research" data-testid="research-link">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Prospect Research
                  </Link>
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem asChild>
                  <Link to="/admin" data-testid="admin-link">
                    <Settings className="w-4 h-4 mr-2" />
                    Admin Panel
                  </Link>
                </DropdownMenuItem>
              )}
              {(isAdmin || user?.role === 'manager') && !isFirstCallExperience && (
                <DropdownMenuItem asChild>
                  <Link to="/team" data-testid="team-link">
                    <Users className="w-4 h-4 mr-2" />
                    Team Management
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <Link to="/billing" data-testid="billing-link">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Billing & Usage
                </Link>
              </DropdownMenuItem>
              {user?.platform_access?.is_owner && (
                <DropdownMenuItem asChild>
                  <Link to="/platform" data-testid="platform-dashboard-link">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Platform Dashboard
                  </Link>
                </DropdownMenuItem>
              )}
              {user?.platform_access?.is_staff && (
                <DropdownMenuItem asChild>
                  <Link to="/platform?tab=tickets" data-testid="platform-tickets-link">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Support Tickets (Platform)
                  </Link>
                </DropdownMenuItem>
              )}
              {isAdmin && isFirstCallExperience && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled className="text-xs text-zinc-500" data-testid="setup-locked-hint">
                    <Users className="w-3 h-3 mr-2" />
                    Team management unlocks after your first call
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <div><SupportButton /></div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        </div>
      </header>

      {/* Pre-call setup or Main dashboard */}
      {!callStarted ? (
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <div data-testid="pre-call-setup">
            <div className="flex flex-col gap-3 mb-8 empty:hidden empty:mb-0">
            {/* Email not verified — non-blocking nudge banner (call-creation gate
                kicks in only AFTER the first call, mirroring backend rule). */}
            {user && user.email_verified === false && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="email-verify-banner">
                <p className="text-sm font-medium text-amber-900">Verify your email to keep going</p>
                <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                  We sent a 6-digit code to <strong>{user.email}</strong>. After your first trial call, you&apos;ll need it to start more.{' '}
                  <Link to="/verify-email" className="underline font-medium" data-testid="verify-email-link">Enter code now →</Link>
                </p>
              </div>
            )}
            {/* First-call welcome hint — only on a fresh org */}
            {isFirstCallExperience && !isViewOnlySeat && (
              <div className="rounded-lg border border-brand-primary/20 bg-brand-accent-muted px-4 py-3" data-testid="first-call-welcome-hint">
                <p className="text-sm font-medium text-brand-primary">Welcome to PipeGuide! 👋</p>
                <p className="text-xs text-zinc-700 mt-1 leading-relaxed">
                  Start your first qualification call below to see the AI in action. Team management unlocks automatically afterwards — workspace settings are in your account menu.
                </p>
              </div>
            )}
            {/* Free tier expiry warning */}
            {planLimits?.plan === 'starter' && planLimits?.free_tier_expires_at && !planLimits?.free_tier_expired && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center" data-testid="free-tier-expiry-warning">
                <p className="text-sm text-amber-800">
                  Free trial expires <strong>{new Date(planLimits.free_tier_expires_at).toLocaleDateString()}</strong>.{' '}
                  {(user?.role === 'admin' || user?.can_view_billing === true) ? (
                    <><Link to="/billing" className="text-amber-900 underline font-medium">Upgrade now</Link> to keep using PipeGuide.</>
                  ) : (
                    <>Ask your admin to upgrade to keep using PipeGuide.</>
                  )}
                </p>
              </div>
            )}
            {planLimits?.free_tier_expired && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="free-tier-expired-banner">
                <div className="flex-1">
                  <p className="text-sm text-red-800 font-medium">Your free trial has expired.</p>
                  <p className="text-sm text-red-700">Upgrade to continue using PipeGuide.</p>
                </div>
                <Link to="/billing" className="shrink-0">
                  <Button size="sm" className="bg-red-600 hover:bg-red-700">
                    <CreditCard className="w-4 h-4 mr-2" />
                    View Plans
                  </Button>
                </Link>
              </div>
            )}

            {/* Resume Call Banner */}
            {resumableSession && (
              <div className="bg-brand-muted border border-brand-accent rounded-lg p-4" data-testid="resume-call-banner">
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-brand-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-brand-primary">Active call in progress</p>
                    <p className="text-sm text-brand-primary mt-0.5">
                      {resumableSession.prospect_name || 'Unknown'} {resumableSession.company_name ? `@ ${resumableSession.company_name}` : ''} — {resumableSession.transcript?.length || 0} messages recorded
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={() => resumeCall(resumableSession)}
                        size="sm"
                        className="bg-brand-primary hover:bg-brand-hover rounded-md"
                        data-testid="resume-call-btn"
                      >
                        <Phone className="w-3.5 h-3.5 mr-1.5" />
                        Resume Call
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-md text-zinc-600"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/sessions/${resumableSession.id}/end`);
                            setResumableSession(null);
                            toast.info('Previous call ended');
                          } catch (e) { setResumableSession(null); }
                        }}
                        data-testid="end-previous-call-btn"
                      >
                        End Previous Call
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            </div>

            {isViewOnlySeat ? (
              <AdminViewOnlyPanel role={user?.role} />
            ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            <div className="lg:col-span-7 xl:col-span-8 flex flex-col gap-6">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
                Start a {companySettings?.call_type_labels?.primary_call || 'Qualification Call'}
              </h1>
              <p className="text-zinc-500 mt-2">
                Set up your call parameters and begin live qualification assistance
              </p>
            </div>

            <div className="bg-white border border-zinc-200 rounded-2xl p-5 sm:p-8 shadow-sm space-y-6">
              {/* Input Mode Toggle — large segmented cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="input-mode-toggle">
                <button
                  onClick={() => setInputMode('meeting')}
                  className={`flex flex-col items-start gap-1.5 p-4 rounded-xl border-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary ${
                    inputMode === 'meeting'
                      ? 'border-brand-primary bg-brand-muted'
                      : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50'
                  }`}
                  data-testid="mode-meeting-btn"
                >
                  <Video className={`w-5 h-5 ${inputMode === 'meeting' ? 'text-brand-primary' : 'text-zinc-400'}`} />
                  <span className={`text-sm font-semibold ${inputMode === 'meeting' ? 'text-brand-primary' : 'text-zinc-700'}`}>Online Meeting</span>
                  <span className="text-xs text-zinc-500 leading-relaxed">A PipeGuide bot joins your Zoom, Teams or Meet call</span>
                </button>
                <button
                  onClick={() => setInputMode('mic')}
                  className={`flex flex-col items-start gap-1.5 p-4 rounded-xl border-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary ${
                    inputMode === 'mic'
                      ? 'border-brand-primary bg-brand-muted'
                      : 'border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50'
                  }`}
                  data-testid="mode-mic-btn"
                >
                  <Mic className={`w-5 h-5 ${inputMode === 'mic' ? 'text-brand-primary' : 'text-zinc-400'}`} />
                  <span className={`text-sm font-semibold ${inputMode === 'mic' ? 'text-brand-primary' : 'text-zinc-700'}`}>In-Person / Phone</span>
                  <span className="text-xs text-zinc-500 leading-relaxed">Listen through this device&apos;s microphone</span>
                </button>
              </div>

              {/* Meeting Link Input (meeting mode only) */}
              {inputMode === 'meeting' && <CalendarMeetings />}
              {inputMode === 'meeting' && (() => {
                const validation = validateMeetingLink(meetingLink);
                const hasInput = meetingLink.trim().length > 0;
                return (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-zinc-700 flex items-center gap-2">
                      <Link2 className="w-4 h-4" />
                      Meeting Link
                    </label>
                    <div className="relative">
                      <Input
                        placeholder="https://us05web.zoom.us/j/1234567890"
                        value={meetingLink}
                        onChange={(e) => setMeetingLink(e.target.value)}
                        onBlur={() => {
                          // Auto-normalize on blur: prepend https://, trim whitespace
                          const v = validateMeetingLink(meetingLink);
                          if (v.normalized && v.normalized !== meetingLink) {
                            setMeetingLink(v.normalized);
                          }
                        }}
                        className={`rounded-md pr-24 ${
                          hasInput && validation.warning ? 'border-amber-300 focus-visible:ring-amber-300' : ''
                        } ${
                          hasInput && validation.platform ? 'border-emerald-300 focus-visible:ring-emerald-300' : ''
                        }`}
                        data-testid="meeting-link-input"
                      />
                      {hasInput && validation.platform && (
                        <div
                          className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full"
                          data-testid="meeting-link-platform-badge"
                        >
                          <Check className="w-3 h-3 text-emerald-600" />
                          <span className="text-xs font-medium text-emerald-700">{validation.platform.name}</span>
                        </div>
                      )}
                    </div>

                    {/* Inline warning — does NOT block submission */}
                    {hasInput && validation.warning && (
                      <div
                        className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md"
                        data-testid="meeting-link-warning"
                      >
                        <AlertCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-800">{validation.warning}</p>
                      </div>
                    )}

                    {/* Persistent "What went wrong?" card — appears after a bot
                        join failure so the rep can see why (instead of a
                        toast that auto-dismisses). One-click to try again. */}
                    {meetingBotStatus === 'error' && meetingBotError && (
                      <div
                        className="border border-red-200 bg-red-50 rounded-md p-3 space-y-2"
                        data-testid="bot-error-card"
                      >
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold uppercase tracking-wide text-red-700 mb-1">
                              The bot couldn&apos;t join
                            </div>
                            <p className="text-sm text-red-800 leading-relaxed">{meetingBotError}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 pl-6">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs rounded-md border-red-300 text-red-700 hover:bg-red-100"
                            onClick={() => {
                              setMeetingBotStatus(null);
                              setMeetingBotError(null);
                              setMeetingBotId(null);
                              setMeetingLink('');
                              // Focus the meeting-link input after a tick so React renders it
                              setTimeout(() => {
                                const el = document.querySelector('[data-testid="meeting-link-input"]');
                                if (el) el.focus();
                              }, 50);
                            }}
                            data-testid="bot-error-try-again-btn"
                          >
                            <Link2 className="w-3 h-3 mr-1.5" />
                            Try a different link
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs rounded-md text-red-700 hover:bg-red-100"
                            onClick={() => setMeetingBotError(null)}
                            data-testid="bot-error-dismiss-btn"
                          >
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Format helper & "what happens next" live in the right-hand context rail */}
                  </div>
                );
              })()}
              {/* Follow-up indicator */}
              {parentSessionId && (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-3 flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-800">
                    Starting a {(companySettings?.call_type_labels?.follow_up_call || 'follow-up call').toLowerCase()} for {prospectName || 'previous qualification'}
                  </span>
                </div>
              )}

              <FrameworkSelector
                frameworks={frameworks}
                selectedFramework={selectedFramework}
                onSelect={setSelectedFramework}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-zinc-700">Prospect Name (optional)</label>
                  <Input
                    placeholder="John Smith"
                    value={prospectName}
                    onChange={(e) => setProspectName(e.target.value)}
                    className="rounded-md"
                    data-testid="prospect-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-zinc-700">Company Name (optional)</label>
                  <Input
                    placeholder="Acme Corp"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="rounded-md"
                    data-testid="company-name-input"
                  />
                  {companyName.trim().length >= 3 && orgOnboarding?.research_enabled !== false && researchLookup && (
                    researchLookup.found ? (
                      <Link
                        to={`/research?open=${researchLookup.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 hover:bg-emerald-100 transition-colors"
                        data-testid="research-available-chip"
                      >
                        <Sparkles className="w-3 h-3" />
                        Research ready for {researchLookup.prospect_company} — view prep
                      </Link>
                    ) : (
                      <Link
                        to={`/research?company=${encodeURIComponent(companyName.trim())}${companyWebsite.trim() ? `&website=${encodeURIComponent(companyWebsite.trim())}` : ''}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-primary bg-brand-muted border border-brand-accent rounded-full px-2.5 py-1 hover:opacity-80 transition-opacity"
                        data-testid="research-this-prospect-btn"
                      >
                        <Sparkles className="w-3 h-3" />
                        No recent research — research this prospect
                      </Link>
                    )
                  )}
                </div>

                {/* Company Website */}
                <div className="space-y-2 sm:col-span-2">
                  <label className="text-sm font-medium text-zinc-700 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Company Website (optional)
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="www.acmecorp.com"
                      value={companyWebsite}
                      onChange={(e) => setCompanyWebsite(e.target.value)}
                      className="rounded-md flex-1"
                      data-testid="company-website-input"
                    />
                    <Button
                      variant="outline"
                      onClick={researchCompany}
                      disabled={isResearchingCompany || !companyWebsite.trim()}
                      className="rounded-md"
                      data-testid="research-company-btn"
                    >
                      {isResearchingCompany ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Building2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-500">
                    Add the prospect&apos;s website to get AI-powered company insights and more relevant questions
                  </p>
                </div>
              </div>

              {/* Company Context Preview */}
              {companyContext && (
                <div className="bg-brand-muted border border-brand-accent rounded-md p-3" data-testid="company-context-preview">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-brand-primary" />
                    <span className="text-sm font-medium text-brand-primary">Company Context Loaded</span>
                  </div>
                  <p className="text-xs text-brand-primary line-clamp-3">{companyContext}</p>
                </div>
              )}

              <Button
                onClick={startCall}
                disabled={inputMode === 'meeting' && !meetingLink.trim()}
                className={`w-full h-14 text-base font-bold rounded-xl shadow-md transition-transform hover:-translate-y-0.5 ${parentSessionId ? 'bg-purple-600 hover:bg-purple-700' : 'bg-brand-primary hover:bg-brand-hover'}`}
                data-testid="start-call-btn"
              >
                {parentSessionId ? (
                  <>
                    <GitBranch className="w-5 h-5 mr-2" />
                    Start {companySettings?.call_type_labels?.follow_up_call || 'Follow-up Call'}
                  </>
                ) : inputMode === 'meeting' ? (
                  <>
                    <Video className="w-5 h-5 mr-2" />
                    Join Meeting & Analyze
                  </>
                ) : (
                  <>
                    <Phone className="w-5 h-5 mr-2" />
                    Start Call Session
                  </>
                )}
              </Button>
            </div>
            </div>

            {/* Right context rail */}
            <div className="lg:col-span-5 xl:col-span-4 flex flex-col gap-4">
              {inputMode === 'meeting' ? (
                <>
                  <div className="bg-[#FAFAFA] border border-zinc-200 rounded-2xl p-6" data-testid="meeting-mode-info">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="w-4 h-4 text-brand-primary" />
                      <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">What happens next</span>
                    </div>
                    <ol className="space-y-4">
                      <li className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-brand-muted text-brand-primary flex items-center justify-center text-xs font-bold shrink-0">1</span>
                        <span className="text-sm text-zinc-600 leading-relaxed">A PipeGuide bot will join your meeting as a participant.</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-brand-muted text-brand-primary flex items-center justify-center text-xs font-bold shrink-0">2</span>
                        <span className="text-sm text-zinc-600 leading-relaxed"><span className="font-medium text-zinc-800">Admit it from the waiting room</span> if your platform prompts you (usually 10–20s after Start).</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-brand-muted text-brand-primary flex items-center justify-center text-xs font-bold shrink-0">3</span>
                        <span className="text-sm text-zinc-600 leading-relaxed">Transcripts and AI coaching start flowing automatically — no setup on your side.</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="w-6 h-6 rounded-full bg-brand-muted text-brand-primary flex items-center justify-center text-xs font-bold shrink-0">4</span>
                        <span className="text-sm text-zinc-600 leading-relaxed">Works with Zoom / Teams / Meet / Webex on both <span className="font-medium text-zinc-800">desktop and web</span>.</span>
                      </li>
                    </ol>
                  </div>

                  {!meetingLink.trim() && (
                    <div className="bg-[#FAFAFA] border border-zinc-200 rounded-2xl p-6" data-testid="meeting-link-helper">
                      <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Supported formats</p>
                      <div className="space-y-2.5">
                        {MEETING_PLATFORMS.map(p => (
                          <div key={p.id}>
                            <p className="text-xs font-medium text-zinc-700">{p.name}</p>
                            <p className="text-xs text-zinc-500 font-mono break-all">{p.example}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-[#FAFAFA] border border-zinc-200 rounded-2xl p-6" data-testid="mic-mode-info">
                  <div className="flex items-center gap-2 mb-4">
                    <Mic className="w-4 h-4 text-brand-primary" />
                    <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">How it works</span>
                  </div>
                  <ol className="space-y-4">
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-brand-muted text-brand-primary flex items-center justify-center text-xs font-bold shrink-0">1</span>
                      <span className="text-sm text-zinc-600 leading-relaxed">PipeGuide listens through this device&apos;s microphone — ideal for in-person meetings and phone calls on speaker.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-brand-muted text-brand-primary flex items-center justify-center text-xs font-bold shrink-0">2</span>
                      <span className="text-sm text-zinc-600 leading-relaxed">Live transcription starts as soon as the call begins.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-brand-muted text-brand-primary flex items-center justify-center text-xs font-bold shrink-0">3</span>
                      <span className="text-sm text-zinc-600 leading-relaxed">The AI scores qualification in real time and suggests the next best probing question.</span>
                    </li>
                  </ol>
                </div>
              )}

              <RecentCallsPeek />
            </div>
            </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
          {/* On mobile: Questions come FIRST (most important for the rep) */}
          {/* On desktop: Left sidebar with metrics */}
          <div className="order-2 lg:order-1 col-span-1 lg:col-span-3 border-t lg:border-t-0 lg:border-r border-zinc-200 max-h-[40vh] lg:max-h-none lg:h-[calc(100vh-4rem)] overflow-y-auto bg-zinc-50/50 p-4 sm:p-6 flex flex-col gap-4 sm:gap-6">
            <FrameworkSelector
              frameworks={frameworks}
              selectedFramework={selectedFramework}
              onSelect={setSelectedFramework}
              disabled={true}
            />
            
            <QualificationMetrics
              scores={analysis?.scores || {}}
              categories={currentFramework?.categories || []}
              overallScore={analysis?.overall_score || 0}
            />

            {/* Conversation Momentum - NEW */}
            {analysis?.conversation_momentum && (
              <div className="space-y-2">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Conversation Focus
                </span>
                <div className="bg-white border border-zinc-200 rounded-md p-3 text-sm text-zinc-700">
                  {analysis.conversation_momentum}
                </div>
              </div>
            )}

            {/* Key Insights */}
            {analysis?.key_insights && analysis.key_insights.length > 0 && (
              <div className="space-y-2">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Key Insights
                </span>
                <div className="space-y-2">
                  {analysis.key_insights.map((insight, i) => (
                    <div key={i} className="bg-white border border-zinc-200 rounded-md p-2 text-sm text-zinc-700">
                      {insight}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Company Context in sidebar during call */}
            {(companyContext || session?.company_context) && (
              <div className="space-y-2">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Company Context
                </span>
                <div className="bg-brand-muted border border-brand-accent-muted rounded-md p-2 text-xs text-brand-primary">
                  {companyContext || session?.company_context}
                </div>
              </div>
            )}
          </div>

          {/* Center - Suggested Questions (FOCAL POINT) + Transcript toggle */}
          <div className="order-1 lg:order-2 col-span-1 lg:col-span-6 border-b lg:border-b-0 lg:border-r border-zinc-200 h-[60vh] lg:h-[calc(100vh-4rem)] flex flex-col bg-white">
            {/* Call controls */}
            <div className="border-b border-zinc-200 p-3 sm:p-4 flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <h2 className="font-semibold text-zinc-900 text-sm sm:text-base truncate" style={{ fontFamily: 'var(--font-heading)' }}>
                  {prospectName || 'Prospect'} {companyName && `@ ${companyName}`}
                </h2>
                <p className="text-xs sm:text-sm text-zinc-500">
                  {transcript.length} messages • {currentFramework?.name}
                  {isAnalyzing && <span className="ml-2 text-brand-primary">• Analyzing...</span>}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {inputMode === 'meeting' ? (
                  <>
                    {meetingBotStatus === 'joining' && (
                      <Button variant="outline" disabled className="rounded-md" data-testid="bot-joining-indicator">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Bot Joining...
                      </Button>
                    )}
                    {['in_meeting', 'in_call'].includes(meetingBotStatus) && (
                      <Button
                        variant="outline"
                        onClick={leaveMeeting}
                        className="rounded-md text-orange-600 border-orange-200 hover:bg-orange-50"
                        data-testid="leave-meeting-btn"
                      >
                        <WifiOff className="w-4 h-4 mr-2" />
                        Remove Bot
                      </Button>
                    )}
                    {['ended', 'done'].includes(meetingBotStatus) && (
                      <Badge variant="outline" className="text-zinc-500 border-zinc-300">Bot Left</Badge>
                    )}
                    {['error', 'fatal'].includes(meetingBotStatus) && (
                      <Badge variant="outline" className="text-red-600 border-red-300">Bot Error</Badge>
                    )}
                  </>
                ) : (
                  <Button
                    variant={isListening ? "destructive" : "default"}
                    onClick={toggleListening}
                    className="rounded-md"
                    data-testid="toggle-listening-btn"
                  >
                    {isListening ? (
                      <>
                        <MicOff className="w-4 h-4 mr-2" />
                        Stop Listening
                      </>
                    ) : (
                      <>
                        <Mic className="w-4 h-4 mr-2" />
                        Start Listening
                      </>
                    )}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={endCall}
                  className="rounded-md text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="end-call-btn"
                >
                  <PhoneOff className="w-4 h-4 mr-2" />
                  End Call
                </Button>
              </div>
            </div>

            {/* Main content: Questions as focal point */}
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
              {/* Suggested Questions - PRIMARY FOCUS */}
              <div className="flex-1 min-h-0 overflow-y-auto p-6 transition-all" data-testid="questions-focal-area">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-brand-primary" />
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                      Ask Next
                    </span>
                  </div>
                  {/* Analysis countdown */}
                  <AnalysisCountdown 
                    isActive={autoAnalysisEnabled && (isListening || ['in_meeting', 'in_call'].includes(meetingBotStatus))}
                    isAnalyzing={isAnalyzing}
                    lastAnalysisTime={lastAnalysisTime}
                  />
                </div>
                <SuggestedQuestions
                  questions={analysis?.suggested_questions || []}
                  gaps={analysis?.gaps || []}
                  pinnedQuestion={pinnedQuestion}
                  onPinQuestion={setPinnedQuestion}
                />
              </div>

              {/* Coaching highlights — questions the rep actually asked.
                  Rewards followers of the coach + gives managers a tangible
                  behavioural signal post-call. Auto-hides when empty. */}
              <CoachingHighlights
                suggestedQuestions={analysis?.suggested_questions || []}
                transcript={transcript}
              />

              {/* Pre-call research peek — collapsible, only when research exists */}
              {callResearch && <ResearchPeekPanel research={callResearch} />}

              {/* Transcript Toggle Bar */}
              <div 
                className="border-t border-zinc-200 px-4 py-2 flex items-center justify-between bg-zinc-50 cursor-pointer hover:bg-zinc-100 transition-colors"
                onClick={() => setShowTranscript(!showTranscript)}
                data-testid="transcript-toggle"
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-zinc-400" />
                  <span className="text-xs font-medium text-zinc-600">
                    Live Transcript ({transcript.length} messages)
                  </span>
                  {isListening && !showTranscript && (
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-xs text-green-600">recording</span>
                    </span>
                  )}
                </div>
                <ChevronUp className={`w-4 h-4 text-zinc-400 transition-transform ${showTranscript ? '' : 'rotate-180'}`} />
              </div>

              {/* Collapsible Transcript */}
              {showTranscript && (
                <div className="flex-1 min-h-0 border-t border-zinc-200 flex flex-col overflow-hidden">
                  <LiveTranscript transcript={transcript} isListening={isListening} inputMode={inputMode} meetingBotStatus={meetingBotStatus} />
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Solution Builder */}
          <div className="order-3 col-span-1 lg:col-span-3 max-h-[40vh] lg:max-h-none lg:h-[calc(100vh-4rem)] overflow-hidden bg-zinc-50/50">
            <Tabs defaultValue="solution" className="h-full flex flex-col">
              <TabsList className="w-full rounded-none border-b border-zinc-200 bg-white">
                {companySettings?.show_solution_tab !== false && (
                  <TabsTrigger 
                    value="solution" 
                    className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-brand-primary"
                    data-testid="solution-tab"
                  >
                    {companySettings?.solution_tab_label || 'Solution'}
                  </TabsTrigger>
                )}
              </TabsList>
              
              {companySettings?.show_solution_tab !== false && (
                <TabsContent value="solution" className="flex-1 p-6 overflow-y-auto m-0">
                  {/* Product Fit Scores */}
                  {analysis?.product_fit && analysis.product_fit.length > 0 && (
                    <div className="mb-4 space-y-2" data-testid="product-fit-panel">
                      <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Product Match</span>
                      <div className="space-y-2">
                        {analysis.product_fit
                          .sort((a, b) => b.fit_score - a.fit_score)
                          .map((pf, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 bg-white border border-zinc-200 rounded-md">
                            <div className={`w-2 h-2 rounded-full ${
                              pf.fit_score >= 70 ? 'bg-emerald-500' : pf.fit_score >= 40 ? 'bg-amber-500' : 'bg-zinc-300'
                            }`} />
                            <span className="text-sm font-medium text-zinc-900 flex-1">{pf.product}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                              pf.fit_score >= 70 ? 'bg-emerald-100 text-emerald-700' :
                              pf.fit_score >= 40 ? 'bg-amber-100 text-amber-700' :
                              'bg-zinc-100 text-zinc-500'
                            }`}>
                              {pf.fit_score}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <SolutionBuilder
                    notes={session?.solution_notes || []}
                    aiRequirements={analysis?.solution_requirements || []}
                    onAddNote={handleAddNote}
                    products={products}
                  />
                </TabsContent>
              )}
            </Tabs>
          </div>
        </div>
      )}

      {/* First-call guided tour — 5-step contextual overlay; auto-dismisses on completion */}
      <FirstCallTour
        active={Boolean(tourActive && callStarted && isFirstCallExperience)}
        steps={TOUR_STEPS}
        onComplete={() => setTourActive(false)}
      />

      {/* Manager Invite Prompt — shown after first call if no manager on team */}
      {showManagerInvitePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="manager-invite-prompt">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-accent-muted rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-brand-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>Great first call!</h3>
                <p className="text-sm text-zinc-500">Earn +30 free minutes — invite your manager</p>
              </div>
            </div>
            <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2">
              <p className="text-sm text-emerald-800 font-medium">
                We&apos;ll add <span className="font-bold">+30 minutes free</span> to your trial as soon as your first manager joins.
              </p>
            </div>
            <p className="text-sm text-zinc-700">
              Coaching reports, team leaderboards, and rep improvement areas all light up when a manager is on the team. Testing alone leaves the best insights on the table.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => { setShowManagerInvitePrompt(false); navigate('/team?invite=manager'); }}
                className="flex-1 bg-brand-primary hover:bg-brand-hover"
                data-testid="invite-manager-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Invite Manager
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowManagerInvitePrompt(false)}
                className="flex-1"
                data-testid="dismiss-invite-prompt"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Token-exhausted modal — shown when /api/meeting/join returns 402 with
          structured detail. Lets the rep email their admin OR switch to the
          In-Person/mic fallback in one click. */}
      <TokenBlockedModal
        info={tokenBlockedInfo}
        onClose={() => setTokenBlockedInfo(null)}
        onSwitchToMic={() => {
          setInputMode('mic');
          setMeetingBotStatus(null);
          setMeetingBotError(null);
          setMeetingLink('');
        }}
      />

      {/* PiP Coaching Window — floats over Zoom/Teams */}
      <PiPCoachingWindow
        isOpen={pipOpen}
        onClose={() => { setPipOpen(false); setPipCallEnded(false); }}
        analysis={analysis}
        isAnalyzing={isAnalyzing}
        isListening={isListening}
        callEnded={pipCallEnded}
        inputMode={inputMode}
        meetingBotStatus={meetingBotStatus}
        onTogglePause={toggleListening}
        onEndCall={endCall}
        prospectName={prospectName}
        companyName={companyName}
        categories={currentFramework?.categories || []}
        pinnedQuestion={pinnedQuestion}
        onPinQuestion={setPinnedQuestion}
      />
    </div>
  );
}
